import * as React from 'react'
import {useEffect, useState, useRef} from 'react'
import {translate} from 'react-i18next'
import {withTheme} from 'styled-components'
import {useRouteMatch} from 'react-router-dom'
import {useScroll} from 'hooks/useScroll'
import config from 'config'
import {ELEMENTS_IDS, BRANDS, LANGUAGE_BY_BRAND, ALL_LANGUAGES} from 'const'
import {compose} from 'utils/reactUtils'
import {isProfileApp} from 'utils/envUtils'
import {getAboutLink} from 'utils/languageUtils'
import {getClient} from 'theme'
import {getBrandComponents} from 'theme/components'

import {Menu, LanguageList, BrandLogo, GlobeIcon} from './components'
import * as StyledComponents from './styled'

const multilanguageBrands = Object.keys(LANGUAGE_BY_BRAND).filter(b => LANGUAGE_BY_BRAND[b].length > 1)

const getLanguageOptions = isProfile => {
  const brand = getClient(window)

  if (isProfile) {
    return ALL_LANGUAGES
  }

  return LANGUAGE_BY_BRAND[brand] || LANGUAGE_BY_BRAND.default
}

const Header = ({t, theme, mobile, logout, email}) => {
  const [shouldShowLanguagesList, setShouldShowLanguagesList] = useState(false)
  const [shouldShowMenu, setShouldShowMenu] = useState(false)
  const isScrolling = useScroll()
  const globeRef = useRef()
  const brand = getClient(window)
  const isProfile = isProfileApp()
  const BrandHeaderView = getBrandComponents(theme.brand.name).HeaderView || StyledComponents.HeaderView
  const isMultilanguageBrandExceptOnelog = multilanguageBrands.filter(b => b !== BRANDS.onelog).includes(brand)
  const isOnelogButNotProfilePage = brand === BRANDS.onelog && !isProfile
  const isInfoPage = useRouteMatch('/info')?.isExact
  const withLanguageSwitcher =
    !isInfoPage && (isMultilanguageBrandExceptOnelog || isOnelogButNotProfilePage || isProfile)
  const languageOptions = getLanguageOptions(isProfile)
  const showPoweredLogo = !isProfile && !theme.shell.header.hidePoweredLogo
  const handleGlobeIconCLick = () => setShouldShowLanguagesList(!shouldShowLanguagesList)
  const toggleMenu = () => setShouldShowMenu(!shouldShowMenu)

  useEffect(() => {
    const handleMousedownEvent = ({target}) => {
      if (shouldShowLanguagesList && globeRef.current && !globeRef.current.contains(target)) {
        setShouldShowLanguagesList(false)
      }
    }

    window.addEventListener('mousedown', handleMousedownEvent, false)

    return () => window.removeEventListener('mousedown', handleMousedownEvent, false)
  }, [shouldShowLanguagesList])

  return (
    <BrandHeaderView isScrolling={isScrolling} email={email}>
      <StyledComponents.HeaderContainer isScrolling={isScrolling} email={email}>
        <StyledComponents.BrandLogoView isScrolling={isScrolling} isProfile={isProfile}>
          <BrandLogo theme={theme} mobile={mobile} isScrolling={isScrolling} />
        </StyledComponents.BrandLogoView>
        <StyledComponents.LanguageSwitcherView>
          {email && (
            <StyledComponents.HeaderEmailView>
              <StyledComponents.HeaderEmailContentView isScrolling={isScrolling}>
                {email}
              </StyledComponents.HeaderEmailContentView>
              <StyledComponents.HeaderEmailSeparator />
              <StyledComponents.HeaderLogout id={ELEMENTS_IDS.profilePage.logout} onClick={logout} type="button">
                {t('header.logout')}
              </StyledComponents.HeaderLogout>
            </StyledComponents.HeaderEmailView>
          )}
          {showPoweredLogo && (
            <StyledComponents.PoweredView>
              <StyledComponents.PoweredLogoLink href={getAboutLink()}>
                <StyledComponents.PoweredLogo src={`${config.url}/assets/${theme.shell.header.poweredLogo.src}`} />
              </StyledComponents.PoweredLogoLink>
            </StyledComponents.PoweredView>
          )}
          {withLanguageSwitcher && (
            <StyledComponents.GlobeButtonView
              ref={globeRef}
              className="language-switcher"
              active={shouldShowLanguagesList}
              onBlur={() => setShouldShowLanguagesList(false)}
              aria-label={t('choose-language')}
              onClick={handleGlobeIconCLick}>
              <GlobeIcon />
              <LanguageList languageOptions={languageOptions} />
            </StyledComponents.GlobeButtonView>
          )}
        </StyledComponents.LanguageSwitcherView>
        {(withLanguageSwitcher || email) && (
          <Menu
            show={shouldShowMenu}
            mobile={mobile}
            toggleMenu={toggleMenu}
            languageOptions={languageOptions}
            isScrolling={isScrolling}
            showLogout={isProfile}
            onLogout={logout}
          />
        )}
      </StyledComponents.HeaderContainer>
    </BrandHeaderView>
  )
}

export default compose(translate(), withTheme)(Header)
